import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Label,
  ResponsiveContainer,
} from "recharts";
import { useEffect, useMemo, useState } from "react";

import { Tab } from "../../../components/tab";
import { DashboardChartPoint } from "./types";
import { useAppDispatch, useAppSelector } from "../../../hooks/hooks";
import {
  DashboardUserActivityPeriod,
  fetchDashboardUserActivity,
  selectDashboardUserActivity,
} from "../../../features/auth/store/dashboardStatSlice";

const AreaGraph = ({ data }: { data: DashboardChartPoint[] }) => (
  <div className="h-[200px] sm:h-[240px] w-full">
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="name">
          <Label
            value="Period"
            position="bottom"
            className="satoshi-xs-medium-gray-600 hidden sm:block"
            style={{ textAnchor: "middle" }}
          />
        </XAxis>
        <YAxis
          width={30}
          domain={[0, Math.max(...data.map((item) => item.value), 0) + 5]}
        >
          <Label
            value="Users"
            position="left"
            angle={-90}
            className="satoshi-xs-medium-gray-600 hidden sm:block"
            style={{ textAnchor: "middle" }}
          />
        </YAxis>
        <Tooltip />
        <Area type="monotone" dataKey="value" stroke="#027EC2" fill="#E6F5FE" />
      </AreaChart>
    </ResponsiveContainer>
  </div>
);

export const UserActivity = () => {
  const dispatch = useAppDispatch();
  const [activeIndex, setActiveIndex] = useState(1);

  const periods: {
    label: string;
    value: DashboardUserActivityPeriod;
  }[] = useMemo(
    () => [
      { label: "12 months", value: "12_months" },
      { label: "30 days", value: "30_days" },
      { label: "7 days", value: "7_days" },
      { label: "24 hours", value: "24_hours" },
    ],
    [],
  );

  const activePeriod = periods[activeIndex]?.value ?? "30_days";
  const userActivity = useAppSelector(
    selectDashboardUserActivity(activePeriod),
  );

  useEffect(() => {
    if (!userActivity.lastUpdated && !userActivity.loading) {
      dispatch(fetchDashboardUserActivity(activePeriod));
    }
  }, [activePeriod, dispatch]);

  const tabs = periods.map((period) => ({
    label: period.label,
    content: userActivity.loading ? (
      <div className="flex h-[200px] sm:h-[240px] items-center justify-center">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
      </div>
    ) : userActivity.error ? (
      <div className="flex h-[200px] sm:h-[240px] items-center justify-center text-sm text-red-500">
        Failed to load user activity
      </div>
    ) : (
      <AreaGraph data={userActivity.data} />
    ),
  }));

  return (
    <div className="rounded-xl border border-gray-200 p-4 sm:p-6 flex flex-col gap-5 flex-1 min-w-0 w-full">
      <div className="flex flex-col gap-4 w-full">
        <span className="satoshi-xl-bold-pry-900 dark:text-white">
          User Activity
        </span>
        <Tab
          initialActiveIndex={1}
          options={tabs}
          onChange={setActiveIndex}
          className="w-full flex flex-col gap-4"
          tabListClassName="border-b border-gray-200 flex items-center gap-2 w-full overflow-x-auto scrollbar-none whitespace-nowrap"
          tabItemClassName="px-2 pb-2 pt-[1px] satoshi-xs-bold-gray-400 fallback-flex-shrink-0"
          activeTabItemClassName="border-b-2 border-pry-700"
        />
      </div>
    </div>
  );
};
